/**
 * WeaponSlot.ts
 *
 * Represents a single weapon slot (Gun, Tractor, or Bomb).
 * Manages weapons assigned to this slot and handles firing.
 */

import Phaser from 'phaser';
import {
  SlotType,
  WeaponState,
  WeaponDefinition,
  WeaponInstance,
  DataManagerWeapon,
  SLOT_KEYCODES,
} from './DataManagerWeapon';

export interface WeaponSlotConfig {
  slotType: SlotType;
  scene: Phaser.Scene;
}

/**
 * WeaponSlot - Manages weapons in a single slot
 */
export class WeaponSlot {
  public readonly slotType: SlotType;
  public readonly keyCode: number;

  private scene: Phaser.Scene;
  private weapons: Map<string, WeaponInstance> = new Map();
  private activeWeaponId: string | null = null;

  // For hold-type weapons (tractor beam)
  private isHolding: boolean = false;
  private holdStartTime: number = 0;

  constructor(config: WeaponSlotConfig) {
    this.slotType = config.slotType;
    this.scene = config.scene;
    this.keyCode = SLOT_KEYCODES[config.slotType];

    // Add base weapon
    this.initializeBaseWeapon();
  }

  /**
   * Initialize the base weapon for this slot
   */
  private initializeBaseWeapon(): void {
    const baseWeapon = DataManagerWeapon.getBaseWeaponForSlot(this.slotType);
    if (baseWeapon) {
      // Pass default ammo for non-infinite base weapons (e.g., plasmabomb)
      const initialAmmo = baseWeapon.hasInfiniteAmmo ? undefined : DataManagerWeapon.getDefaultPickupAmmo(baseWeapon.id);
      const instance = DataManagerWeapon.createWeaponInstance(baseWeapon.id, initialAmmo);
      if (instance) {
        instance.state = 'active';
        this.weapons.set(baseWeapon.id, instance);
        this.activeWeaponId = baseWeapon.id;
      }
    }
  }

  /**
   * Get the active weapon
   */
  getActiveWeapon(): WeaponInstance | undefined {
    if (!this.activeWeaponId) return undefined;
    return this.weapons.get(this.activeWeaponId);
  }

  /**
   * Get active weapon definition (always reads fresh from DataManagerWeapon for hot-reload support)
   */
  getActiveWeaponDefinition(): WeaponDefinition | undefined {
    const weapon = this.getActiveWeapon();
    if (!weapon) return undefined;
    // Read fresh definition from manager (supports hot-reload)
    return DataManagerWeapon.get(weapon.definition.id) ?? weapon.definition;
  }

  /**
   * Add a weapon to this slot
   */
  addWeapon(weaponId: string, initialAmmo?: number): boolean {
    const weaponDef = DataManagerWeapon.get(weaponId);
    if (!weaponDef || weaponDef.slot !== this.slotType) {
      console.warn(`Cannot add ${weaponId} to ${this.slotType} slot`);
      return false;
    }

    // Check if weapon already exists
    const existing = this.weapons.get(weaponId);
    if (existing) {
      // Just add ammo
      if (!weaponDef.hasInfiniteAmmo) {
        const ammoToAdd = initialAmmo ?? DataManagerWeapon.getDefaultPickupAmmo(weaponId);
        existing.ammo += ammoToAdd;
      }
      return true;
    }

    // Create new weapon instance
    const instance = DataManagerWeapon.createWeaponInstance(
      weaponId,
      initialAmmo ?? DataManagerWeapon.getDefaultPickupAmmo(weaponId)
    );

    if (instance) {
      instance.state = 'active';
      this.weapons.set(weaponId, instance);
      return true;
    }

    return false;
  }

  /**
   * Set the active weapon
   */
  setActiveWeapon(weaponId: string): boolean {
    const weapon = this.weapons.get(weaponId);
    if (!weapon) {
      console.warn(`Weapon ${weaponId} not in slot ${this.slotType}`);
      return false;
    }

    if (weapon.state !== 'active') {
      console.warn(`Weapon ${weaponId} is not active`);
      return false;
    }

    this.activeWeaponId = weaponId;
    return true;
  }

  /**
   * Cycle to next weapon in slot
   */
  cycleWeapon(): string | undefined {
    const activeWeapons = Array.from(this.weapons.entries())
      .filter(([_, w]) => w.state === 'active')
      .map(([id]) => id);

    if (activeWeapons.length <= 1) return this.activeWeaponId ?? undefined;

    const currentIndex = activeWeapons.indexOf(this.activeWeaponId ?? '');
    const nextIndex = (currentIndex + 1) % activeWeapons.length;

    this.activeWeaponId = activeWeapons[nextIndex];
    return this.activeWeaponId;
  }

  /**
   * Check if weapon can fire
   */
  canFire(time: number): boolean {
    const weapon = this.getActiveWeapon();
    if (!weapon) return false;

    // Get fresh definition (supports hot-reload)
    const def = DataManagerWeapon.get(weapon.definition.id) ?? weapon.definition;

    // Check state
    if (weapon.state !== 'active') return false;

    // Check ammo
    if (!def.hasInfiniteAmmo && weapon.ammo <= 0) {
      return false;
    }

    // Check fire rate cooldown
    const timeSinceLastFire = time - weapon.lastFiredAt;
    if (timeSinceLastFire < def.fireRate) {
      return false;
    }

    return true;
  }

  /**
   * Fire the weapon
   * Returns fire points and projectile info for spawning
   */
  fire(
    time: number,
    playerX: number,
    playerY: number
  ): {
    fired: boolean;
    projectileType?: string;
    firePoints: Array<{ x: number; y: number }>;
    angle: number;
    speed: number;
    damage: number;
    burst?: {
      count: number;
      delay: number;
      initialScales?: Array<{ x: number; y: number }>;
    };
  } | null {
    if (!this.canFire(time)) return null;

    const weapon = this.getActiveWeapon();
    if (!weapon) return null;

    // Get fresh definition (supports hot-reload)
    const def = DataManagerWeapon.get(weapon.definition.id) ?? weapon.definition;

    // Update last fired
    weapon.lastFiredAt = time;

    // Consume ammo
    if (!def.hasInfiniteAmmo) {
      weapon.ammo--;
    }

    // Calculate absolute fire points
    const firePoints = def.firePoints.map((fp) => ({
      x: playerX + fp.x,
      y: playerY + fp.y,
    }));

    return {
      fired: true,
      projectileType: def.projectileType,
      firePoints,
      angle: def.fireAngle, // From weapon definition (extracted from C3)
      speed: def.projectileSpeed,
      damage: def.damage,
      burst: def.burst,
    };
  }

  /**
   * Start holding (for tractor beam)
   */
  startHold(time: number): void {
    this.isHolding = true;
    this.holdStartTime = time;
  }

  /**
   * Stop holding
   */
  stopHold(): void {
    this.isHolding = false;
  }

  /**
   * Check if currently holding
   */
  getIsHolding(): boolean {
    return this.isHolding;
  }

  /**
   * Get hold duration
   */
  getHoldDuration(time: number): number {
    if (!this.isHolding) return 0;
    return time - this.holdStartTime;
  }

  /**
   * Add ammo to a weapon
   */
  addAmmo(weaponId: string, amount: number): boolean {
    const weapon = this.weapons.get(weaponId);
    if (!weapon) return false;

    // Get fresh definition (supports hot-reload)
    const def = DataManagerWeapon.get(weapon.definition.id) ?? weapon.definition;
    if (def.hasInfiniteAmmo) return true;

    weapon.ammo += amount;
    return true;
  }

  /**
   * Get ammo for a weapon
   */
  getAmmo(weaponId?: string): number {
    const id = weaponId ?? this.activeWeaponId;
    if (!id) return 0;

    const weapon = this.weapons.get(id);
    if (!weapon) return 0;

    // Get fresh definition (supports hot-reload)
    const def = DataManagerWeapon.get(weapon.definition.id) ?? weapon.definition;
    return def.hasInfiniteAmmo ? -1 : weapon.ammo;
  }

  /**
   * Get all weapons in this slot
   */
  getWeapons(): WeaponInstance[] {
    return Array.from(this.weapons.values());
  }

  /**
   * Get all active weapons
   */
  getActiveWeapons(): WeaponInstance[] {
    return Array.from(this.weapons.values()).filter(
      (w) => w.state === 'active'
    );
  }

  /**
   * Check if slot has a specific weapon
   */
  hasWeapon(weaponId: string): boolean {
    return this.weapons.has(weaponId);
  }

  /**
   * Get slot state for saving
   */
  getState(): {
    activeWeaponId: string | null;
    weapons: Array<{ id: string; state: WeaponState; ammo: number }>;
  } {
    return {
      activeWeaponId: this.activeWeaponId,
      weapons: Array.from(this.weapons.entries()).map(([id, w]) => ({
        id,
        state: w.state,
        ammo: w.ammo,
      })),
    };
  }

  /**
   * Load slot state
   */
  loadState(state: {
    activeWeaponId: string | null;
    weapons: Array<{ id: string; state: WeaponState; ammo: number }>;
  }): void {
    this.weapons.clear();

    for (const weaponState of state.weapons) {
      const instance = DataManagerWeapon.createWeaponInstance(weaponState.id, weaponState.ammo);
      if (instance) {
        instance.state = weaponState.state;
        instance.ammo = weaponState.ammo;
        this.weapons.set(weaponState.id, instance);
      }
    }

    this.activeWeaponId = state.activeWeaponId;
  }
}
